import React, { useEffect } from 'react';
import { Alert, Card, Table, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import { usePageLayout } from '../hooks/usePageLayout';
import { isSuperAdmin } from '../utils/roleUtils';

const { Title, Paragraph, Text } = Typography;

const marketColumns = [
    { title: 'Market', dataIndex: 'market', key: 'market', width: 90 },
    { title: 'Address mode', dataIndex: 'addressMode', key: 'addressMode', width: 110 },
    { title: 'Currency', dataIndex: 'currency', key: 'currency', width: 90 },
    { title: 'Calculation method', dataIndex: 'calculationMethod', key: 'calculationMethod', width: 150 },
    { title: 'Locale', dataIndex: 'locale', key: 'locale', width: 90 },
    { title: 'Recommended CO2 factor', dataIndex: 'co2Factor', key: 'co2Factor', width: 170 },
    { title: 'Notes', dataIndex: 'notes', key: 'notes' },
];

const marketRows = [
    {
        key: 'uk',
        market: 'UK',
        addressMode: 'postcode',
        currency: 'GBP',
        calculationMethod: 'kkTable',
        locale: 'en',
        co2Factor: 'leave blank',
        notes: 'Default UK setup. Use kkTable for standard UK quoting.',
    },
    {
        key: 'ie',
        market: 'IE',
        addressMode: 'postcode',
        currency: 'EUR',
        calculationMethod: 'pvgis',
        locale: 'en',
        co2Factor: '0.295',
        notes: 'Irish market with EUR pricing. PVGIS recommended.',
    },
    {
        key: 'de',
        market: 'DE',
        addressMode: 'postcode',
        currency: 'EUR',
        calculationMethod: 'pvgis',
        locale: 'de',
        co2Factor: '0.366',
        notes: 'German language flow and PVGIS irradiance data.',
    },
    {
        key: 'us',
        market: 'US',
        addressMode: 'zip',
        currency: 'USD',
        calculationMethod: 'pvgis',
        locale: 'en',
        co2Factor: 'optional',
        notes: 'Set utility-rate defaults (state, import, export) for US fallback pricing.',
    },
];

const Step = ({ number, children }) => (
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 10 }}>
        <div style={{
            minWidth: 24,
            height: 24,
            borderRadius: '50%',
            background: '#4c6ef5',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12,
            fontWeight: 700,
            marginTop: 2,
        }}>
            {number}
        </div>
        <div style={{ flex: 1 }}>{children}</div>
    </div>
);

export default function CustomerOnboardingGuide() {
    const navigate = useNavigate();
    const pageLayoutProps = usePageLayout();
    const { role } = pageLayoutProps;

    useEffect(() => {
        if (role && !isSuperAdmin(role)) {
            navigate('/');
        }
    }, [role, navigate]);

    if (!isSuperAdmin(role)) {
        return null;
    }

    return (
        <PageLayout {...pageLayoutProps}>
            <div style={{ maxWidth: 980, margin: '0 auto' }}>
                <Title level={2} style={{ marginBottom: 8 }}>Super Admin Customer Onboarding Guide</Title>
                <Paragraph type='secondary' style={{ marginBottom: 18 }}>
                    Living runbook for adding a new customer property. Update this page whenever onboarding steps change.
                </Paragraph>

                <Alert
                    type='info'
                    showIcon
                    message='Scope'
                    description='This guide is for super admins adding or configuring a customer account in Property Manager, then validating calculator readiness for UK, IE, DE, and US deployments.'
                    style={{ marginBottom: 20 }}
                />

                <Card title='Quick Path' style={{ marginBottom: 20 }}>
                    <Step number={1}><Text strong>Open Property Manager</Text>: Sidebar {'->'} Property Manager.</Step>
                    <Step number={2}><Text strong>Create property</Text>: Add Property and complete required fields.</Step>
                    <Step number={3}><Text strong>Apply market preset</Text>: Use the table below for UK/IE/DE/US defaults.</Step>
                    <Step number={4}><Text strong>Enable features</Text>: Toggle residential, EV, battery, finance, commercial, lead gate, PDF mode, and form layout.</Step>
                    <Step number={5}><Text strong>Complete branding and contact details</Text>: Logo/colors/font and contact metadata.</Step>
                    <Step number={6}><Text strong>Finish operational setup</Text>: Variables, rules, PDF content/layout, email config, images.</Step>
                    <Step number={7}><Text strong>Create user access</Text>: Add admin/user and assign property.</Step>
                    <Step number={8}><Text strong>Validate</Text>: Open Calculator Preview and perform a full quote flow test.</Step>
                </Card>

                <Card title='Market Presets (Recommended Defaults)' style={{ marginBottom: 20 }}>
                    <Table
                        columns={marketColumns}
                        dataSource={marketRows}
                        pagination={false}
                        size='small'
                        scroll={{ x: 'max-content' }}
                    />
                </Card>

                <Card title='Detailed Step-by-Step' style={{ marginBottom: 20 }}>
                    <Step number={1}>
                        <Text strong>Create the property</Text>
                        <div>Go to Property Manager {'->'} Add Property.</div>
                    </Step>
                    <Step number={2}>
                        <Text strong>Enter base identity fields</Text>
                        <div>Property ID (optional; auto-generated if blank), Company Name, Currency, Market, Address mode, Calculation method, Locale.</div>
                    </Step>
                    <Step number={3}>
                        <Text strong>Set market-sensitive values</Text>
                        <div>Avg annual consumption, CO2 factor, and for US: utility state and default import/export rates.</div>
                    </Step>
                    <Step number={4}>
                        <Text strong>Configure branding</Text>
                        <div>Primary color, heading color, text color, and font family under Calculator Branding.</div>
                    </Step>
                    <Step number={5}>
                        <Text strong>Configure features</Text>
                        <div>Residential, EV defaults, battery rules, finance defaults, commercial rates, lead gate behavior, PDF mode, and form layout.</div>
                    </Step>
                    <Step number={6}>
                        <Text strong>Save and reopen property</Text>
                        <div>Save changes, then edit the property again to confirm values persisted correctly.</div>
                    </Step>
                    <Step number={7}>
                        <Text strong>Configure pricing logic</Text>
                        <div>Open Cost Variables and Calculation Rules for this property. Ensure one active rule is set.</div>
                    </Step>
                    <Step number={8}>
                        <Text strong>Configure customer artifacts</Text>
                        <div>Update PDF content/layout/designer, Image Manager assets, and Email Configuration.</div>
                    </Step>
                    <Step number={9}>
                        <Text strong>Create user access</Text>
                        <div>Use Add New User and assign Admin/User role to the new property.</div>
                    </Step>
                    <Step number={10}>
                        <Text strong>Run functional checks</Text>
                        <div>Use Calculator Preview and test quote flow, add-ons, PDF output, and lead submission.</div>
                    </Step>
                    <Step number={11}>
                        <Text strong>Run analytics checks</Text>
                        <div>Confirm events and leads appear in Dashboard/Audit/Leads for the new property.</div>
                    </Step>
                    <Step number={12}>
                        <Text strong>Deployment handoff</Text>
                        <div>Provide propertyID to website/widget env variables and verify embedded calculator/widget pages load in the expected locale.</div>
                    </Step>
                </Card>

                <Card title='Validation Checklist' style={{ marginBottom: 20 }}>
                    <Paragraph style={{ marginBottom: 8 }}>Before handoff, verify all items below:</Paragraph>
                    <ul style={{ lineHeight: 1.9, paddingLeft: 20 }}>
                        <li>Property appears in Property Manager list and can be selected from the super admin property dropdown.</li>
                        <li>Feature toggles match commercial requirements for that customer.</li>
                        <li>At least one calculation rule is active for the property.</li>
                        <li>PDF output is branded and email config is correct.</li>
                        <li>Lead capture path works and lead records are visible in Leads page.</li>
                        <li>Calculator Preview returns expected currency/locale and realistic outputs.</li>
                        <li>Website/widget integration uses the correct propertyID.</li>
                    </ul>
                </Card>

                <Alert
                    type='warning'
                    showIcon
                    message='Maintenance Note'
                    description='Treat this as the source-of-truth runbook. Update this page when fields, defaults, or onboarding steps change in Property Manager or API behavior.'
                />
            </div>
        </PageLayout>
    );
}
